import { createContext, useState } from "react";
import { createAccountDetail, updateAccountDetail } from "../graphql/mutations";
import { API, Auth } from "aws-amplify";
import {
  basicAttribute,
  cognitoManageNames,
  judgeStatus,
  manualInputRequiredKeys,
  nationalCodeList,
  requiredKeys,
} from "../config/rewardSetting";
import { getAccountDetail } from "../graphql/queries";
import { fetchAndUpdateEkyc, fetchFailedEkyc } from "../util/eKyc";
import VALIDATE_REWARD_SETTING from "../util/validateRewardSetting";
import fetchRewards from "../util/fetchRewards";

const init = {
  user: null,
  bank: {
    saved: null,
    invalidBank: false,
    inputted: {
      bankName: "",
      bankCode: "",
      branchName: "",
      branchCode: "",
    },
  },
  settingParam: {
    address: null,
    nationalCode: nationalCodeList[0].value,
    postCode: null,
  },
  pageStatus: {
    isLoaded: false,
    isInputted: false,
    open: false,
    modalOpen: false,
    isManualInputAddress: false,
  },
};

export const RewardSettingContext = createContext(init);

export default function RewardSettingProvider({ children }) {
  const [user, setUser] = useState(null);
  const [settingParam, setSettingParam] = useState({
    address: null,
    nationalCode: nationalCodeList[0].value,
    postCode: null,
  });
  const [pageStatus, setPageStatus] = useState({
    isLoaded: false,
    isInputted: false,
    open: false,
    modalOpen: false,
    isManualInputAddress: false,
  });
  const [bank, setBank] = useState({
    saved: null,
    invalidBank: false,
    inputted: {
      bankName: "",
      bankCode: "",
      branchName: "",
      branchCode: "",
    },
  });
  const [formInput, setFormInput] = useState({});
  const [inputtedAddress, setInputtedAddress] = useState(null);
  const [failedData, setFailedData] = useState(null);

  const [basicInfo, setBasicInfo] = useState({
    familyNameKana: "",
    givenNameKana: "",
    phoneNumber: "",
    gender: "",
    addressFirst: "",
    addressSecond: "",
  });

  const setUserDataToForm = (user) => {
    if (user.attributes) {
      setSettingParam((prev) => ({
        ...prev,
        address: user.attributes.address || null,
        nationalCode:
          user.attributes["custom:national_code"] || nationalCodeList[0].value,
      }));
    }
  };

  const load = {
    start: () => {
      setPageStatus((prev) => ({
        ...prev,
        isLoaded: true,
      }));
    },
    end: () => {
      setPageStatus((prev) => ({
        ...prev,
        isLoaded: false,
      }));
    },
  };

  /**
 * 以前の報酬履歴から口座起因で失敗しているものがあるか判定
 * @param {*} user 
 * @returns {boolean} 失敗していたらtrue, 失敗していなかったらfalse
 */
  const judgeFailedTransfer = async (user) => {
    if (!user || !user.attributes) return false;
    const sub = user.attributes.sub;
    const rewards = await fetchRewards(sub);
    if (!rewards || !rewards.length) return false;
    const failedResults = rewards.some((reward) => {
      if (reward.judgeStatus === judgeStatus.is_inputted_bank.value) return true;
      if (reward.judgeStatus === judgeStatus.invalid_bank.value) return true;
      if (reward.judgeStatus === judgeStatus.failed_transfer.value) return true;
      return false;
    });
    return failedResults;
  };

  const fetchAndSetAccountDetail = async (user) => {
    const account = await API.graphql({
      query: getAccountDetail,
      variables: {
        user_id: user.attributes.sub,
      },
    });

    const bankInfo = account.data.getAccountDetail;
    if (bankInfo) {
      const isFailedTransferByInvalidBank = await judgeFailedTransfer(user);
      const { bank_name, bank_code, branch_name, branch_code } = bankInfo;
      setBank((prev) => ({
        ...prev,
        saved: bankInfo || null,
        invalidBank: isFailedTransferByInvalidBank,
        inputted: {
          bankName: bank_name,
          bankCode: bank_code,
          branchName: branch_name,
          branchCode: branch_code,
        },
      }));
    }
    return bankInfo;
  };

  const initIsManualInputAddress = (user) => {
    if (!user || !user.attributes) return true;
    const failedToOcr = !user.attributes["custom:ocr_address"];
    if (failedToOcr) return true;
    const isBeforeInputtedManualAddress = user.attributes.address;
    if (isBeforeInputtedManualAddress) return true;
    return false;
  };

  const getUserInfo = async () => {
    try {
      load.start();
      const user = await Auth.currentAuthenticatedUser();
      setUser(user);
      const bankInfo = await fetchAndSetAccountDetail(user);
      setUserDataToForm(user);
      await fetchAndUpdateEkyc(user);
      const failedResults = await fetchFailedEkyc(user);
      if (failedResults && Object.keys(failedResults).length > 0) {
        setFailedData(failedResults);
      }
      setPageStatus((prev) => ({
        ...prev,
        isInputted: !!bankInfo,
        isLoaded: false,
        // 証明書が読み取れていなかった場合、住所を手動入力するようにする
        isManualInputAddress: initIsManualInputAddress(user),
      }));
    } catch (error) {
      console.log(error);
      alert("ユーザー情報の取得に失敗しました。");
      load.end();
    }
  };

  const handleSubmit = async (e) => {

    const formatData = (data) => {
      const formatted = {};
      Object.entries(data).forEach(([key, value]) => {
        if (key === "birth_year") {
          const birthMonth = data["birth_month"].toString().padStart(2, "0");
          const birthDay = data["birth_day"].toString().padStart(2, "0");
          const birthValue = `${value}-${birthMonth}-${birthDay}`;
          formatted["birthdate"] = birthValue;
        } else if (key === "birth_month" || key === "birth_day") {
          return;
        } else if (key === "phone_number") {
          formatted[key] = settingParam.nationalCode + value;
        } else {
          formatted[key] = value;
        }
      });
      return formatted;
    };

    const validation = (data) => {

      const isInvalidAccountNum = () => {
        if (!bank.saved) return true;
        if (!bank.saved.account_num) return true;
        const validate = VALIDATE_REWARD_SETTING.ACCOUNT_NUM(bank.saved.account_num);
        if (validate) {
          return true;
        }
        return false;
      }

      const isInvalidAccountPersonName = () => {
        if (!bank.saved) return true;
        if (!bank.saved.account_person_name) return true;
        const validate = VALIDATE_REWARD_SETTING.ACCOUNT_PERSON_NAME(bank.saved.account_person_name);
        if (validate) {
          return true;
        }
        return false;
      }

      const isNoCheck = (key) => {
        if (key === "account_person_name" && isInvalidAccountPersonName()) {
          return false;
        }
        if (key === "account_num" && isInvalidAccountNum()) {
          return false;
        }
        return true;
      }

      const validateForm = () => {
        const requiredKeysLength = requiredKeys.length;
        let errorMessages = [];
        const isInvalid = {
          accountPersonName: isInvalidAccountPersonName(),
          accountNum: isInvalidAccountNum(),
        };

        for (let i = 0; i < requiredKeysLength; i++) {
          const key = requiredKeys[i];
          if (!data[key] && !isNoCheck(key)) {
            errorMessages.push(`${key}は必須項目です。`);
          }
          if (key === "gender") {
            const validate = VALIDATE_REWARD_SETTING.GENDER(data[key]);
            if (validate) {
              errorMessages.push(validate);
            }
          }
          if (key === "account_person_name" && isInvalid.accountPersonName) {
            const validate = VALIDATE_REWARD_SETTING.ACCOUNT_PERSON_NAME(data[key]);
            if (validate) {
              errorMessages.push(validate);
            }
          }
          if (key === "account_num" && isInvalid.accountNum) {
            const validate = VALIDATE_REWARD_SETTING.ACCOUNT_NUM(data[key]);
            if (validate) {
              errorMessages.push(validate);
            }
          }
        }
        if (pageStatus.isManualInputAddress) {
          for (let i = 0; i < manualInputRequiredKeys.length; i++) {
            const key = manualInputRequiredKeys[i];
            if (!data[key]) {
              errorMessages.push(`${key}は必須項目です。`);
            }
          }
        }
        return errorMessages;
      };
      const validateState = () => {
        const errorMessages = [];
        if (!settingParam.nationalCode) {
          errorMessages.push("国籍を選択してください。");
        }
        if (data.gender === "title") {

          errorMessages.push("性別を選択してください。");
        }
        if (!bank.inputted.bankName) {
          errorMessages.push("銀行名を入力してください。");
        }
        if (!bank.inputted.branchName) {
          errorMessages.push("支店名を入力してください。");
        }
        if (!bank.inputted.bankCode) {
          errorMessages.push("銀行を選択してください");
        }
        if (!bank.inputted.branchCode) {
          errorMessages.push("支店を選択してください");
        }
        return errorMessages;
      };

      const formErrors = validateForm();
      const stateErrors = validateState();
      const errors = [...formErrors, ...stateErrors];
      if (errors.length > 0) {
        throw new Error(errors.join("\n"));
      }
    };

    const formatSelfInformation = (data) => {
      const cognitoManageData = Object.fromEntries(
        Object.entries(data).filter(([key, value]) =>
          cognitoManageNames.includes(key)
        )
      );
      const attr = {};
      Object.entries(cognitoManageData).forEach(([key, value]) => {
        if (basicAttribute.includes(key)) {
          attr[key] = value;
        } else {
          attr[`custom:${key}`] = value;
        }
      });
      if (settingParam.address) {
        attr.address = settingParam.address;
      }
      attr.updated_at = Math.floor(Date.now() / 1000).toString();
      attr["custom:national_code"] = settingParam.nationalCode;
      return attr;
    };

    const saveSelfInformation = async (data) => {
      const attr = formatSelfInformation(data);
      await Auth.updateUserAttributes(user, attr);
      const updatedUser = await Auth.currentAuthenticatedUser();
      setUser(updatedUser);
    };

    const saveBankInformation = async (data) => {
      const create = () => {
        const bankData = {};
        Object.entries(data).forEach(([key, value]) => {
          if (key === "group-select-address-input-way") return null;
          if (!cognitoManageNames.includes(key)) {
            bankData[key] = value;
          }
        });

        bankData.bank_code = bank.inputted.bankCode;
        bankData.branch_code = bank.inputted.branchCode;
        bankData.bank_name = bank.inputted.bankName;
        bankData.branch_name = bank.inputted.branchName;
        return bankData;
      };
      const bankData = create();
      await API.graphql({
        query: pageStatus.isInputted
          ? updateAccountDetail
          : createAccountDetail,
        variables: {
          input: {
            ...bankData,
            user_id: user.attributes.sub,
          },
        },
      });
    };

    const pickFormData = (e) => {
      const formData = new FormData(e.target);
      const data = Object.fromEntries(formData);
      validation(data);
      return data;
    };

    const save = async (data) => {
      const formatted = formatData(data);
      setFormInput(data);
      await saveSelfInformation(formatted);
      await saveBankInformation(formatted);
    };

    const closer = async () => {
      setPageStatus((prev) => ({
        ...prev,
        open: false,
      }));
      await fetchAndSetAccountDetail(user);
      alert("入力いただいた情報を保存しました。");
    };

    try {
      e.preventDefault();
      load.start();
      const data = pickFormData(e);
      await save(data);
      setPageStatus((prev) => ({
        ...prev,
        isLoaded: false,
        open: true,
      }));
      await closer();
    } catch (error) {
      console.log(error);
      load.end();
      if (error.message) {
        alert(error.message);
      } else {
        alert("情報の保存に失敗しました。");

      }
    }
  };

  const handleClose = () => {
    setPageStatus((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleNationalCode = (e, value) => {
    setSettingParam((prev) => ({
      ...prev,
      nationalCode: value,
    }));
  };

  const clearAddress = () => {
    if (settingParam.address) {
      setSettingParam((prev) => ({
        ...prev,
        address: "",
      }));
    };
  }

  const setBankName = (name, code) => {
    setBank((prev) => ({
      ...prev,
      inputted: {
        ...prev.inputted,
        bankName: name,
        bankCode: code,
      },
    }));
  };

  const setBranchName = (name, code) => {
    setBank((prev) => ({
      ...prev,
      inputted: {
        ...prev.inputted,
        branchName: name,
        branchCode: code,
      },
    }));
  };

  const clearBankInput = () => {
    setBank((prev) => ({
      ...prev,
      inputted: {
        ...prev.inputted,
        bankName: "",
        bankCode: "",
        branchName: "",
        branchCode: "",
      },
    }));
  };

  const clearBankBranch = () => {
    setBank((prev) => ({
      ...prev,
      inputted: {
        ...prev.inputted,
        branchName: "",
        branchCode: "",
      },
    }));
  };

  const handleModal = {
    open: () => {
      setPageStatus((prev) => ({
        ...prev,
        modalOpen: true,
      }));
    },
    close: () => {
      setPageStatus((prev) => ({
        ...prev,
        modalOpen: false,
      }));
    },
  };

  const setAddress = (address) => {
    setSettingParam((prev) => ({
      ...prev,
      address: address,
    }));
  }

  const handleChangeToInputAddressWay = (e) => {
    const { value } = e.target;
    const isManualInputAddress = value === "manual";
    setPageStatus((prev) => ({
      ...prev,
      isManualInputAddress,
    }));
  }

  const changePostCode = (value) => {
    setSettingParam((prev) => ({
      ...prev,
      postCode: value,
    }));
  }
  const handleInputtedAddress = (value) => {
    setInputtedAddress(value);
  }


  const values = {
    user,
    setUser,
    bank,
    settingParam,
    pageStatus,
    formInput,
    getUserInfo,
    failedData,
    setAddress,
    setUserDataToForm,
    handleSubmit,
    handleClose,
    handleNationalCode,
    clearAddress,
    setBankName,
    setBranchName,
    clearBankInput,
    clearBankBranch,
    handleModal,
    handleChangeToInputAddressWay,
    changePostCode,
    inputtedAddress,
    handleInputtedAddress,
    basicInfo,
    setBasicInfo
  };


  return (
    <RewardSettingContext.Provider value={values}>
      {children}
    </RewardSettingContext.Provider>
  );
}
